/* ============================================================
   useSchemaObjects — schema 단위 테이블·뷰 라이브 조회
   2026-05-12 신설 (SchemaView 라이브화 트리거)

   본 hook은 v_dashboard_meta_full을 schema별 필터링하여
   tables + views 분리 + 라벨·desc 라이브 조회.

   Phase 2 row count 라이브화 시 별도 RPC 추가 영역.
   ============================================================ */
import { useEffect, useState } from 'react'
import { supabase, SUPABASE_AVAILABLE } from './supabase'

export interface SchemaObject {
  table_name: string
  object_kind: 'table' | 'view'
  display_label: string | null
  description: string | null
  display_order: number | null
  is_active: boolean | null
}

export interface SchemaObjectsResult {
  tables: SchemaObject[]
  views: SchemaObject[]
  ready: boolean
  error?: string
}

const INITIAL: SchemaObjectsResult = {
  tables: [], views: [], ready: false
}

export function useSchemaObjects(schemaName: string): SchemaObjectsResult {
  const [result, setResult] = useState<SchemaObjectsResult>(INITIAL)

  useEffect(() => {
    if (!SUPABASE_AVAILABLE || !supabase) {
      setResult({ ...INITIAL, ready: true, error: 'env_missing' })
      return
    }
    if (!schemaName) {
      setResult({ ...INITIAL, ready: true })
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const { data, error } = await supabase
          .schema('mg_system')
          .from('v_dashboard_meta_full')
          .select('table_name, object_kind, display_label, description, display_order, is_active')
          .eq('schema_name', schemaName)
          .eq('is_active', true)
          .order('display_order', { ascending: true, nullsFirst: false })
          .order('table_name', { ascending: true })

        if (cancelled) return
        if (error) {
          setResult({ ...INITIAL, ready: true, error: error.message })
          return
        }

        const rows = (data ?? []) as SchemaObject[]
        const tables = rows.filter(r => r.object_kind === 'table')
        const views = rows.filter(r => r.object_kind === 'view')

        setResult({ tables, views, ready: true })
      } catch (e) {
        if (!cancelled) {
          setResult({ ...INITIAL, ready: true, error: String(e) })
        }
      }
    })()

    return () => { cancelled = true }
  }, [schemaName])

  return result
}
